import React, { useState } from 'react';
import { Table, TableColumnsType, Button, Space } from 'antd';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import PendingFilterArea, { PendingFilterOptions, DEFAULT_PENDING_FILTER_OPTIONS } from './components/PendingFilterArea';
import {
    GET_ADDRESS_RESIDENTIAL_AREA_REPORT_LISTING,
  GetAddressResidentialAreaListingResponse,
  AddressResidentialAreaType,
} from './graphql/queries/getAddressResidentialAreaReportListing';
import AddAddressResidentialAreaDrawer from './components/AddAddressResidentialAreaDrawer';
import { AddressResidentialAreaDetailType } from './graphql/queries/getAddressResidentialAreaDetail';
import { useTranslation } from 'react-i18next';
import { HasResource } from '../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';
import AddressResidentialAreaReportDrawer from "./components/AddressResidentialAreaReportDrawer";
import AddressResidentialAreaReviewReportDrawer from "./components/AddressResidentialAreaReviewReportDrawer";

const AddressResidentialAreaPendingPage: React.FC = () => {
  const [appliedFilters, setAppliedFilters] = useState<PendingFilterOptions>(DEFAULT_PENDING_FILTER_OPTIONS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [drawerMode, setDrawerMode] = useState<'view' | 'edit'>('edit');
  const [drawerAllowSwitchToEdit, setDrawerAllowSwitchToEdit] = useState(false);
  const [reviewDrawerVisible, setReviewDrawerVisible] = useState(false);
  const [reviewAreaId, setReviewAreaId] = useState<string | null>(null);
  const [addDrawerVisible, setAddDrawerVisible] = useState(false);
  const [addDrawerInitialValues, setAddDrawerInitialValues] = useState<
    Parameters<typeof AddAddressResidentialAreaDrawer>[0]['initialValues']
  >(undefined);
  const [drawerReportedImages, setDrawerReportedImages] = useState<
    Array<{ imageS3Key: string; imageDisplayUrl?: string | null }> | undefined
  >(undefined);
  const [drawerReportDataId, setDrawerReportDataId] = useState<string | undefined>(undefined);
  const [drawerDataSource, setDrawerDataSource] = useState<'report' | 'area'>('report');
  const { t } = useTranslation();

  const { data, loading, refetch } = useQuery<GetAddressResidentialAreaListingResponse>(
      GET_ADDRESS_RESIDENTIAL_AREA_REPORT_LISTING,
    {
      variables: {
        residentialAddress: appliedFilters.residentialAddress || undefined,
        creatorId: appliedFilters.creatorId ?? undefined,
        status: appliedFilters.status !== 'all' ? appliedFilters.status : undefined,
        createdAtRange: (() => {
          const range = appliedFilters.createdAtRange;
          if (range && Array.isArray(range) && range[0] && range[1]) {
            return {
              start: range[0].startOf('day').toISOString(),
              end: range[1].endOf('day').toISOString(),
            };
          }
          return undefined;
        })(),
        page: currentPage,
        size: pageSize,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const handleFilter = (options: PendingFilterOptions) => {
    setAppliedFilters(options);
    setCurrentPage(1);
  };

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const openDetailDrawer = (record: AddressResidentialAreaType, mode: 'view' | 'edit') => {
    setSelectedAreaId(record.id);
    setDrawerMode(mode);
    setDrawerAllowSwitchToEdit(mode === 'edit');
    setDrawerDataSource('report');
    setDrawerVisible(true);
  };

  const statusText = (status: string | number | null | undefined): string => {
    const key = String(status ?? '').toLowerCase();
    const map: Record<string, string> = {
      pending: t('mapTool.addressLibrary.locationCorrectionPage.status.pending'),
      approved: t('mapTool.addressLibrary.locationCorrectionPage.status.approved'),
      rejected: t('mapTool.addressLibrary.locationCorrectionPage.status.rejected'),
    };
    return map[key] ?? String(status ?? '—');
  };

  const columns: TableColumnsType<AddressResidentialAreaType> = [
    {
      title: t('mapTool.addressResidentialArea.table.id'),
      dataIndex: 'id',
      key: 'id',
      width: 100,
      align: 'center',
      render: (id: string, record: AddressResidentialAreaType) => (
        <Button
          type="link"
          size="small"
          className="p-0 h-auto font-normal"
          onClick={() => openDetailDrawer(record, 'view')}
        >
          {id}
        </Button>
      ),
    },
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.address'),
      dataIndex: 'residentialAddress',
      key: 'residentialAddress',
      width: 360,
      ellipsis: true,
    },
    {
      title: t('mapTool.addressResidentialArea.table.standardizedAddress'),
      dataIndex: 'standardAddress',
      key: 'standardAddress',
      width: 360,
      ellipsis: true,
    },
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.reporterAccount'),
      dataIndex: 'creatorAccount',
      key: 'creatorAccount',
      width: 160,
      ellipsis: true,
      render: (v: string | null | undefined) => v ?? '—',
    },
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.reportedAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      align: 'center',
      render: (v: string | null | undefined) =>
        v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—',
    },
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      align: 'center',
      render: (v: string | number | null | undefined) => statusText(v),
    },
    {
      title: t('components.button.action') as string,
      key: 'action',
      width: 140,
      fixed: 'right' as const,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          {String(record.status).toUpperCase() === 'PENDING' && (
            <HasResource resourceCodes={[ResourceCode.addressResidentialArea.UPDATE]}>
              <Button
                type="link"
                size="small"
                onClick={() => {
                  setReviewAreaId(record.id);
                  setReviewDrawerVisible(true);
                }}
              >
                {t('mapTool.addressLibrary.locationCorrectionPage.actions.review')}
              </Button>
            </HasResource>
          )}
          <Button
            type="link"
            size="small"
            onClick={() => openDetailDrawer(record, 'view')}
          >
            {t('mapTool.addressLibrary.locationCorrectionPage.actions.view')}
          </Button>
        </Space>
      ),
    },
  ];

  const pageData = data?.addressResidentialAreaReportList;

  return (
    <div className="p-6">
      <h4 className="text-xl font-bold mb-4">{t('mapTool.addressResidentialArea.pendingPageTitle')}</h4>

      <PendingFilterArea filters={appliedFilters} handleFilter={handleFilter} />

      <div className="bg-white rounded-lg shadow-sm mt-4">
        <Table
          columns={columns}
          dataSource={pageData?.content || []}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: pageData?.totalElements || 0,
            showSizeChanger: true,
            showTotal: (total) => t('mapTool.addressResidentialArea.table.paginationTotal', { total }),
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
        />
      </div>

      <AddressResidentialAreaReportDrawer
        visible={drawerVisible}
        areaId={selectedAreaId}
        mode={drawerMode}
        maxDisplayImages={3}
        imagesSectionTitle={drawerMode === 'view' ? t('mapTool.addressResidentialArea.reviewDrawer.sectionImagesTitle') as string : undefined}
        reportedImages={drawerReportedImages}
        reportDataId={drawerReportDataId}
        dataSource={drawerDataSource}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedAreaId(null);
          setDrawerReportedImages(undefined);
          setDrawerReportDataId(undefined);
          setDrawerDataSource('report');
          refetch();
        }}
        onSwitchToEdit={drawerAllowSwitchToEdit ? () => setDrawerMode('edit') : undefined}
      />

      <AddressResidentialAreaReviewReportDrawer
        visible={reviewDrawerVisible}
        areaId={reviewAreaId}
        onClose={() => {
          setReviewDrawerVisible(false);
          setReviewAreaId(null);
        }}
        onRefresh={() => refetch()}
        onApprove={(area: AddressResidentialAreaDetailType) => {
          setAddDrawerInitialValues({
            residentialAddress: area.residentialAddress,
            standardAddress: area.standardAddress,
            stateAbbreviation: area.stateAbbreviation ?? '',
            cityName: area.cityName ?? '',
            zipcode: area.zipcode ?? '',
            residentialName: area.residentialName,
            residentialAddressHash: area.residentialAddressHash ?? '',
            reportDataId: area.id,
            images: area.images
              ?.filter((img) => !!img.imageS3Key)
              .map((img) => ({ key: img.imageS3Key, previewUrl: img.imageDisplayUrl ?? null })),
          });
          setAddDrawerVisible(true);
        }}
        onSwitchToEdit={(existingAreaId: string, imgs, reportDataId) => {
          setSelectedAreaId(existingAreaId);
          setDrawerMode('edit');
          setDrawerAllowSwitchToEdit(false);
          setDrawerReportedImages(imgs);
          setDrawerReportDataId(reportDataId);
          setDrawerDataSource('area');
          setDrawerVisible(true);
        }}
      />

      <AddAddressResidentialAreaDrawer
        visible={addDrawerVisible}
        initialValues={addDrawerInitialValues}
        onClose={() => {
          setAddDrawerVisible(false);
          setAddDrawerInitialValues(undefined);
        }}
        onSuccess={() => refetch()}
      />
    </div>
  );
};

export default AddressResidentialAreaPendingPage;